import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  getAuth,
  onAuthStateChanged,
  signOut,
  User,
} from 'firebase/auth';

import { scoringApp } from '../firebase';

// Auth instance bound exclusively to the scoring Firebase app.
// Completely isolated from the owner-coach auth on the default app.
const scoringAuth = getAuth(scoringApp);

interface ScoringAuthContextType {
  scoringUser: User | null;
  scoringLogout: () => Promise<void>;
  loading: boolean;
}

const ScoringAuthContext = createContext<ScoringAuthContextType | null>(null);

export function useScoringAuth(): ScoringAuthContextType {
  const ctx = useContext(ScoringAuthContext);
  if (!ctx) {
    throw new Error('useScoringAuth must be used within a ScoringAuthProvider');
  }
  return ctx;
}

export function ScoringAuthProvider({ children }: { children: React.ReactNode }) {
  const [scoringUser, setScoringUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  function scoringLogout(): Promise<void> {
    return signOut(scoringAuth).then(() => {});
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(scoringAuth, (user) => {
      setScoringUser(user);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value: ScoringAuthContextType = {
    scoringUser,
    scoringLogout,
    loading,
  };

  return (
    <ScoringAuthContext.Provider value={value}>
      {!loading && children}
    </ScoringAuthContext.Provider>
  );
}
